import { SoAPool } from './SoAPool';

/** Archétypes du J1. Le bestiaire complet arrive avec la machine à contenu
 *  (J4) ; ici trois suffisent à charger le socle de façon représentative. */
export const Kind = {
  Mite: 0, // popcorn : traverse, ne tire pas
  Eclat: 1, // tire une balle visée
  Faucheur: 2, // se poste et tire en éventail de 3
} as const;

export class EnemyPool {
  readonly slots: SoAPool;

  readonly x: Float32Array;
  readonly y: Float32Array;
  readonly px: Float32Array;
  readonly py: Float32Array;
  readonly vx: Float32Array;
  readonly vy: Float32Array;
  /** Hurtbox — 120 % du visuel (PLAN.md §6.2). L'asymétrie est un levier de
   *  game feel, pas de l'assistance : le joueur perçoit « mon tir porte »,
   *  jamais « le jeu m'aide ». */
  readonly r: Float32Array;
  readonly hp: Float32Array;
  readonly maxHp: Float32Array;
  readonly kind: Uint8Array;
  /** Temps restant avant le prochain tir, en secondes. */
  readonly fireCd: Float32Array;
  /** Âge en secondes. Sert aux trajectoires paramétriques et à l'accélération
   *  d'entrée : un ennemi entre à 40 % de sa vitesse et l'atteint en ~0,5 s,
   *  pour laisser le temps de le catégoriser (PLAN.md §6.5). */
  readonly age: Float32Array;
  readonly score: Float32Array;
  /** ⚠️ Télégraphe restant, en secondes (PLAN.md §6.5). Tant qu'il est > 0,
   *  l'ennemi est VISIBLE mais ni touchable ni dangereux : il ne tire pas et
   *  les tirs du joueur le traversent.
   *
   *  Le rendre intouchable, et pas seulement inoffensif, est délibéré — sinon
   *  le joueur détruit les ennemis avant de les avoir vus, et le télégraphe ne
   *  télégraphie plus rien. */
  readonly telegraph: Float32Array;
  /** Phase courante d'un émetteur en anneau, en radians. Stockée par entité :
   *  la rotation par volée est ce qui empêche deux anneaux successifs de se
   *  superposer et de devenir un mur illisible. */
  readonly emitPhase: Float32Array;
  /** Phase de COMBAT courante (boss) : 0 = comportement de base (`def.emitter`),
   *  1..N = `def.phases[phase-1]` (voir `PhaseDef`/`phaseSystem`). Reste à 0 pour
   *  tout ennemi sans `phases` — coût nul là où il n'y a pas de phase. À ne pas
   *  confondre avec `emitPhase` (rotation d'anneau), qui n'a aucun rapport. */
  readonly phase: Uint8Array;

  /* ── Parties destructibles ────────────────────────────────────────────
     Une tourelle de boss est un ENNEMI ORDINAIRE qui se trouve avoir un parent.
     Elle réutilise donc tout : collisions, rendu instancié, télégraphe,
     émetteurs, butin. La détruire coupe son tir sans tuer le porteur — ce qui
     est exactement le comportement voulu.

     ⚠️ `parent` seul ne suffirait pas : un slot libéré est recyclé, et un
     enfant pointerait alors vers un ennemi entièrement différent, hérité par
     accident. D'où `uid`, un identifiant monotone : l'enfant vérifie que le
     parent est bien CELUI qu'il croit. */
  readonly uid: Int32Array;
  /** Slot du parent, ou -1. */
  readonly parent: Int32Array;
  /** `uid` attendu du parent — invalide le lien si le slot a été recyclé. */
  readonly parentUid: Int32Array;

  /* ── Chevauchement d'OBSTACLE (plateforme volante) ────────────────────────
     Un ennemi peut être monté sur un OBSTACLE (le radeau/la plateforme) plutôt
     que sur un autre ennemi. Il le SUIT alors où qu'il aille (repositionné dans
     `obstacleSystem`, après le déplacement des plateformes), et part avec lui
     quand il sort de l'écran. Distinct de `parent` (un autre ennemi) : un
     chevaucheur a `parent = -1` et réutilise `offX`/`offY` pour son décalage sur
     la plateforme. */
  /** Slot de l'obstacle porteur, ou -1. */
  readonly obCarrier: Int32Array;
  /** `uid` attendu du porteur — invalide le lien si le slot a été recyclé. */
  readonly obCarrierUid: Int32Array;
  /** Décalage par rapport au parent, en unités. */
  readonly offX: Float32Array;
  readonly offY: Float32Array;
  /** 1 si l'entité ne peut être blessée tant qu'il lui reste des parties
   *  vivantes — le motif classique « casser les armes avant le noyau ». */
  readonly shielded: Uint8Array;

  /** Décalage de RECUL, en unités, empilé sur la trajectoire et amorti à chaque
   *  tick (voir `motionSystem`). Séparé de `x`/`y` — et non baké dedans — parce
   *  que les trajectoires intégrantes (Linear, Hover…) relisent leur propre
   *  position au tick suivant : un recul baké s'y intégrerait et deviendrait
   *  permanent, transformant une bousculade en dérive. */
  readonly kbx: Float32Array;
  readonly kby: Float32Array;
  /** Vitesse du ressort de recul, en u/s. Le recul n'est pas une décroissance
   *  exponentielle (vitesse maximale à l'impact, puis qui ralentit : que de la
   *  décélération) mais un RESSORT AMORTI : le coup déplace d'un coup, puis
   *  l'ennemi revient à sa place en accélérant depuis l'arrêt et en décélérant
   *  à l'arrivée, avec un léger dépassement. Il faut donc mémoriser sa vitesse
   *  d'une frame à l'autre — un ressort sans vitesse n'est pas un ressort. */
  readonly kbvx: Float32Array;
  readonly kbvy: Float32Array;

  /* ── Bouclier DIRECTIONNEL (PLAN.md §6.4, E08) ────────────────────────
     ⚠️ Sans aucun rapport avec `shielded` ci-dessus, malgré le nom voisin.
     `shielded` conditionne SI l'on peut blesser ; ce qui suit conditionne
     DEPUIS OÙ. Les deux peuvent coexister sur la même entité sans interférer.

     Ces trois tableaux sont publics et c'est intentionnel : la vue en a besoin
     pour dessiner l'arc protecteur au bon endroit, avec la bonne ouverture et
     la bonne intensité. Un bouclier qu'on ne voit pas n'est pas une mécanique,
     c'est un bug — le joueur constate seulement que ses tirs « ne marchent
     pas », ce qui est la pire chose qu'un shmup puisse lui dire. */

  /** PV restants du bouclier. 0 = pas de bouclier, ou bouclier tombé. */
  readonly shieldHp: Float32Array;
  /** PV du bouclier à la naissance. La vue en tire un ratio ; sans lui elle ne
   *  pourrait pas montrer que le bouclier s'use, et casser 800 PV sans le
   *  moindre retour visuel se ressent comme un mur, pas comme un progrès. */
  readonly shieldMaxHp: Float32Array;
  /** DEMI-ouverture du cône protégé, en RADIANS. Stockée en radians et non en
   *  degrés parce que c'est la simulation qui la consomme à chaque impact ; la
   *  vue, qui la lit une fois par frame, peut se permettre la conversion. */
  readonly shieldHalf: Float32Array;

  /* ── Ponte / éclosion (voir `broodSystem`) ────────────────────────────
     Deux colonnes, jamais un objet par entité : la règle du pool. Elles ne
     servent qu'aux rares porteurs qui ont un `brood` sur leur définition et
     restent à zéro pour tous les autres — c'est pourquoi le coût est nul là où
     il n'y a rien à engendrer. */

  /** Temps ACCUMULÉ depuis la dernière ponte, en secondes. Un accumulateur (et
   *  non un compte à rebours) pour que la première ponte tombe `every` secondes
   *  après la fin du télégraphe, sans champ « déjà initialisé » à gérer. */
  readonly broodCd: Float32Array;
  /** Nombre d'engendrés déjà produits. Comparé à `brood.max` : c'est la BORNE
   *  qui empêche un pondeur de saturer le pool. */
  readonly broodDone: Int32Array;

  private nextUid = 1;

  constructor(capacity: number) {
    this.slots = new SoAPool(capacity);
    this.x = new Float32Array(capacity);
    this.y = new Float32Array(capacity);
    this.px = new Float32Array(capacity);
    this.py = new Float32Array(capacity);
    this.vx = new Float32Array(capacity);
    this.vy = new Float32Array(capacity);
    this.r = new Float32Array(capacity);
    this.hp = new Float32Array(capacity);
    this.maxHp = new Float32Array(capacity);
    this.kind = new Uint8Array(capacity);
    this.fireCd = new Float32Array(capacity);
    this.age = new Float32Array(capacity);
    this.score = new Float32Array(capacity);
    this.telegraph = new Float32Array(capacity);
    this.emitPhase = new Float32Array(capacity);
    this.phase = new Uint8Array(capacity);
    this.uid = new Int32Array(capacity);
    this.parent = new Int32Array(capacity).fill(-1);
    this.parentUid = new Int32Array(capacity);
    this.obCarrier = new Int32Array(capacity).fill(-1);
    this.obCarrierUid = new Int32Array(capacity);
    this.offX = new Float32Array(capacity);
    this.offY = new Float32Array(capacity);
    this.shielded = new Uint8Array(capacity);
    this.kbx = new Float32Array(capacity);
    this.kby = new Float32Array(capacity);
    this.kbvx = new Float32Array(capacity);
    this.kbvy = new Float32Array(capacity);
    this.shieldHp = new Float32Array(capacity);
    this.shieldMaxHp = new Float32Array(capacity);
    this.shieldHalf = new Float32Array(capacity);
    this.broodCd = new Float32Array(capacity);
    this.broodDone = new Int32Array(capacity);
  }

  /** Équipe `slot` d'un bouclier directionnel. Appelée à l'apparition, à partir
   *  de la définition de contenu — le pool ne connaît pas les définitions. */
  equipShield(slot: number, hp: number, halfAngleRad: number): void {
    this.shieldHp[slot] = hp;
    this.shieldMaxHp[slot] = hp;
    this.shieldHalf[slot] = halfAngleRad;
  }

  /** Rattache `child` à `parent`, en mémorisant son décalage courant. */
  attach(child: number, parent: number, offX: number, offY: number): void {
    this.parent[child] = parent;
    this.parentUid[child] = this.uid[parent]!;
    this.offX[child] = offX;
    this.offY[child] = offY;
  }

  /** Monte `rider` sur un OBSTACLE porteur (plateforme), avec son décalage. Le
   *  `carrierUid` fige l'identité du porteur : si son slot est recyclé, le lien se
   *  rompt et le chevaucheur est libéré (voir `obstacleSystem`). */
  ride(rider: number, carrier: number, offX: number, offY: number, carrierUid: number): void {
    this.obCarrier[rider] = carrier;
    this.obCarrierUid[rider] = carrierUid;
    this.offX[rider] = offX;
    this.offY[rider] = offY;
  }

  /** Le lien parent est-il encore valide ? Faux si le parent est mort ou si son
   *  slot a été recyclé par un autre ennemi. */
  hasLivingParent(slot: number): boolean {
    const p = this.parent[slot]!;
    if (p < 0) return false;
    if (!this.slots.isAlive(p)) return false;
    return this.uid[p] === this.parentUid[slot];
  }

  /** Nombre de parties vivantes rattachées à `slot`. */
  livingParts(slot: number): number {
    let n = 0;
    const myUid = this.uid[slot]!;
    for (let i = 0; i < this.count; i++) {
      const s = this.at(i);
      if (this.parent[s] === slot && this.parentUid[s] === myUid) n++;
    }
    return n;
  }

  get count(): number {
    return this.slots.count;
  }
  at(i: number): number {
    return this.slots.at(i);
  }

  spawn(
    kind: number,
    x: number,
    y: number,
    hp: number,
    r: number,
    score: number,
    fireCd: number,
    telegraph: number,
  ): number {
    const s = this.slots.alloc();
    if (s < 0) return -1;
    this.x[s] = x;
    this.y[s] = y;
    this.px[s] = x;
    this.py[s] = y;
    this.vx[s] = 0;
    this.vy[s] = 0;
    this.r[s] = r;
    this.hp[s] = hp;
    this.maxHp[s] = hp;
    this.kind[s] = kind;
    this.fireCd[s] = fireCd;
    this.age[s] = 0;
    this.score[s] = score;
    this.telegraph[s] = telegraph;
    this.emitPhase[s] = 0;
    // Un slot recyclé qui garderait la phase de combat de son prédécesseur
    // naîtrait dans une phase avancée — un ennemi frais tirant déjà son attaque
    // d'enrage. Remise à la phase de base OBLIGATOIRE.
    this.phase[s] = 0;
    // ⚠️ Réinitialisation OBLIGATOIRE des liens : un slot recyclé qui
    // conserverait son ancien parent produirait une entité téléportée aux
    // côtés d'un ennemi avec lequel elle n'a rien à voir.
    this.uid[s] = this.nextUid++;
    this.parent[s] = -1;
    this.parentUid[s] = 0;
    // Un slot recyclé qui garderait le lien de chevauchement de son prédécesseur
    // se collerait à un obstacle avec lequel il n'a rien à voir.
    this.obCarrier[s] = -1;
    this.obCarrierUid[s] = 0;
    this.offX[s] = 0;
    this.offY[s] = 0;
    this.shielded[s] = 0;
    // Un slot recyclé qui garderait le recul de son prédécesseur naîtrait
    // légèrement décalé de sa trajectoire, puis reviendrait dessus — un sursaut
    // à l'apparition que rien n'expliquerait.
    this.kbx[s] = 0;
    this.kby[s] = 0;
    this.kbvx[s] = 0;
    this.kbvy[s] = 0;
    // ⚠️ Même exigence que pour les liens de parenté : un slot recyclé qui
    // garderait le bouclier de son prédécesseur produirait une Mite invulnérable
    // par en dessous. Le joueur verrait ses tirs disparaître sur un ennemi qui
    // n'a aucune raison d'en absorber, sans qu'aucun rendu ne l'explique — et
    // le défaut serait proportionnel au recyclage, donc absent des tests courts.
    this.shieldHp[s] = 0;
    this.shieldMaxHp[s] = 0;
    this.shieldHalf[s] = 0;
    // ⚠️ Même exigence que les liens de parenté et le bouclier : un slot recyclé
    // qui garderait le compteur de ponte de son prédécesseur pondrait aussitôt
    // (broodDone déjà à `max`, ou à l'inverse relancerait une horloge en cours).
    this.broodCd[s] = 0;
    this.broodDone[s] = 0;
    return s;
  }

  free(slot: number): void {
    this.slots.free(slot);
  }
  clear(): void {
    this.slots.clear();
  }

  savePrev(): void {
    const n = this.slots.count;
    for (let i = 0; i < n; i++) {
      const s = this.slots.at(i);
      this.px[s] = this.x[s]!;
      this.py[s] = this.y[s]!;
    }
  }
}
